"use client";

import { c as _c } from "react/compiler-runtime";
import { CalendarClock, CheckCircle2, MapPin, User } from "lucide-react";
import { SummaryPanel, SummaryRow } from "@/src/components/online-scheduling/summary";
import { formatLocality, type OnlineSchedulingLocation } from "@/src/components/online-scheduling/types";
import { Button } from "@/src/components/shadcn/ui/button";
import { H2, Muted } from "@/src/components/typography";
import { formatSlotDateTime } from "@/src/lib/scheduling/slot-display";
export function SuccessStep(t0) {
  const $ = _c(45);
  const {
    location,
    slotStart,
    providerDisplayName,
    patientName,
    onBookAnother
  } = t0;
  let t1;
  if ($[0] !== location) {
    t1 = formatLocality(location);
    $[0] = location;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const locality = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10"><CheckCircle2 className="h-8 w-8 text-success" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== patientName) {
    t3 = <H2 className="text-foreground">You're all set, {patientName}!</H2>;
    $[3] = patientName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Muted className="block">Your appointment is confirmed.</Muted>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <div className="space-y-2">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CalendarClock className="h-4 w-4" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== slotStart) {
    t7 = formatSlotDateTime(slotStart);
    $[9] = slotStart;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <p className="font-medium">{t7}</p>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== providerDisplayName) {
    t9 = providerDisplayName && <p className="text-muted-foreground">with {providerDisplayName}</p>;
    $[13] = providerDisplayName;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <SummaryRow icon={t6} label="Appointment">{t8}{t9}</SummaryRow>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <MapPin className="h-4 w-4" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== location.displayName) {
    t12 = <p className="font-medium">{location.displayName}</p>;
    $[19] = location.displayName;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== location.addressLine1) {
    t13 = location.addressLine1 && <p className="text-muted-foreground">{location.addressLine1}</p>;
    $[21] = location.addressLine1;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== locality) {
    t14 = locality && <p className="text-muted-foreground">{locality}</p>;
    $[23] = locality;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t13 || $[27] !== t14) {
    t15 = <SummaryRow icon={t11} label="Location">{t12}{t13}{t14}</SummaryRow>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <User className="h-4 w-4" />;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== patientName) {
    t17 = <p className="font-medium">{patientName}</p>;
    $[30] = patientName;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="text-muted-foreground">Arrive 15 minutes early with a photo ID and insurance card.</p>;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17) {
    t19 = <SummaryRow icon={t16} label="Patient">{t17}{t18}</SummaryRow>;
    $[33] = t17;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t10 || $[36] !== t15 || $[37] !== t19) {
    t20 = <SummaryPanel>{t10}{t15}{t19}</SummaryPanel>;
    $[35] = t10;
    $[36] = t15;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== onBookAnother) {
    t21 = <Button variant="outline" onClick={onBookAnother}>Book another appointment</Button>;
    $[39] = onBookAnother;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21 || $[43] !== t5) {
    t22 = <div className="flex flex-col items-center gap-6 text-center">{t2}{t5}{t20}{t21}</div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t5;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  return t22;
}